import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { useApp } from "@/hooks/use-app-context";
import { isLanguageProfileIncomplete } from "@/lib/language-profile";
import { AccountCard, DeleteAccountCard } from "./AccountCard";
import { DeleteAccountDialog } from "./DeleteAccountDialog";
import { LanguagesSheet, PasswordSheet, ProfileSheet } from "./EditSheets";
import { IdentityCard } from "./IdentityCard";
import { PreferencesCard } from "./PreferencesCard";
import { PrivacySheet } from "./PrivacySheet";
import { settingsCopyFor } from "./settings-copy";
import { useSettings } from "./use-settings";

/**
 * The contributor's profile and settings on one page: who they are, how the
 * app behaves for them, and their account. Every edit opens a sheet.
 */
export function SettingsPage() {
  const { t, user, language } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const copy = settingsCopyFor(language);
  const state = useSettings(copy);
  const { openSheet } = state;

  // The "complete your languages" banner links to ?tab=profile: open the
  // languages sheet straight away when they are still missing.
  const deepLinked = useRef(false);
  useEffect(() => {
    if (deepLinked.current || !user || searchParams.get("tab") !== "profile") return;
    deepLinked.current = true;
    if (user.requiresLanguageSetup ?? isLanguageProfileIncomplete(user)) openSheet("languages");
  }, [user, searchParams, openSheet]);

  if (!user) {
    return (
      <div className="mx-auto max-w-xl px-4 pt-16">
        <EmptyState
          icon="lock"
          title={t.protectedAccess}
          description={t.protectedAccessDesc}
          action={<Button onClick={() => navigate(`/${language}/login`)}>{t.login}</Button>}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 pb-16 pt-8 sm:px-6 lg:px-8">
      <PageHeader title={copy.title} description={copy.subtitle} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
        <div className="space-y-4">
          <IdentityCard user={user} copy={copy} onEdit={() => openSheet("profile")} />
          <AccountCard user={user} copy={copy} onPassword={() => openSheet("password")} onPrivacy={() => openSheet("privacy")} />
        </div>
        <PreferencesCard user={user} copy={copy} onEditLanguages={() => openSheet("languages")} />
      </div>

      <DeleteAccountCard
        copy={copy}
        onDelete={() => state.deletion.setOpen(true)}
        isDeleting={state.isDeleting}
      />

      <ProfileSheet state={state} copy={copy} />
      <LanguagesSheet state={state} copy={copy} />
      <PasswordSheet state={state} copy={copy} />
      <PrivacySheet state={state} copy={copy} />
      <DeleteAccountDialog state={state} />
    </div>
  );
}
